import React, {
  Fragment,
  Suspense,
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { PulseLoader } from "../../components/pulse_loader";

import { LightSpace, getMaxCapacity, PricingType } from "../../data";
import { useGetPartnerOffering } from "../../data/api";
import {
  getMeetingRoomFilterConfig,
  PartnerOffering,
  PartnerOfferingLocation,
  SortOrder,
  Template,
  toFilterLocationFromOfferingLocation,
} from "../../data/partner_offering";
import {
  buildPathWithParams,
  useMediaQuery,
  useQueryString,
} from "../../hooks";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { groupBy, uniqueBy } from "../../lib/js_utils/array_utils";
import { isEmpty } from "../../lib/js_utils/lang_utils";
import {
  usePartnerBookingHourQuery,
  usePartnerBookingPolicyQuery,
  usePartnerOfferingFilterConfigsQuery,
  useSpacesQueryCallback,
} from "../../offering/api";
import { PartnerConfig } from "../../offering/partner_config";
import { Spacer } from "../../offering/spacer";
import { FeaturedCapacity } from "../../offering/template_offering_listing_base";
import {
  deserializeMeetingRoomsFilterSearchParams,
  getFilterConfig,
  MeetingRoomsFilter,
  GalleryFilterSearchParams,
} from "../../offering/template_offering_listing_meeting_room";
import { TemplateOfferingListingLayout } from "./template_offering_listing_base";
import { Button, Card } from "react-bootstrap";
import { useToast } from "../../components";
import { OfferingRibbon } from "../components/offering_ribbon";
import { isEqual, isNil, keys } from "lodash";
import InfiniteScroll from "react-infinite-scroller";
import { MobileFilters } from "../components/filters/mobile_filters";
import { useFilters } from "../hooks/use_filters";
import {
  useMrGalleryImprovementsV2FeatureFlag,
  useShowMeetingRoomGalleryBanner,
} from "../../core/splitio";
import { EmptyMeetingRooms } from "../components/empty_meeting_rooms";
import { DesktopFilters } from "../components/filters/desktop_filters";
import { BulkDiscountBar } from "../components/bulk_discount_bar";
import { useBulkHourlyDiscountQuery } from "../hooks/use_bulk_hourly_discount_query";
import { getSpaceBulkDiscount } from "../utils/get_space_bulk_discount";
import { GalleryRoomCardDetails } from "../components/gallery_room_card_details";
import { useAnalytics } from "../../lib/analytics/analytics";
import { COMING_FROM_GALLERY } from "../hooks/use_handle_press_back";
import { useGetPartnerEventLayouts } from "../hooks/use_get_partner_event_layouts";
import { GalleryDesktopFilters } from "../components/filters/gallery_desktop_filters";
import { GalleyMobileFilters } from "../components/filters/gallery_mobile_filters";
import { SimilarRooms } from "../components/similar_rooms";
import { DynamicPricingAdjustmentType } from "../hooks/use_dynamic_rates_query";
import { ContactUsPrompt } from "../components/talk_with_us_prompt";

interface TemplateOfferingListingGalleryProps {
  partnerConfig: PartnerConfig;
  partnerId: string;
  /** used to override offeringId from URL */
  offeringId?: string;
  partnerName?: string;
  locationAdress?: string;
}

interface TemplateOfferingListingGalleryParams {
  offeringID: string;
  locationID?: string;
  partnerName?: string;
  locationAdress?: string;
}

export function TemplateOfferingListingGallery(
  props: TemplateOfferingListingGalleryProps
) {
  const { partnerConfig, partnerId, offeringId, partnerName, locationAdress } =
    props;
  const toast = useToast();
  const analytics = useAnalytics();
  const { fbt } = useI18n();
  const { device } = useMediaQuery();
  const isMobile = device === "mobile";

  const meetingRoomGalleryImprovementsV2FeatureFlag =
    useMrGalleryImprovementsV2FeatureFlag();

  const showMeetingRoomGalleryBannerFeatureFlag =
    useShowMeetingRoomGalleryBanner(partnerId);

  const { galleryFilterConfig: filterConfig } = partnerConfig;
  const pageParams = useParams<TemplateOfferingListingGalleryParams>();
  const history = useHistory();
  const searchParams = useQueryString<GalleryFilterSearchParams>();
  const [contactUsOpen, setContactUsOpen] = useState(false);
  // There are 2 sources of offeringID: from the Catalog Gallery template page and from the URL
  // This is to support both sources with offeringId from prop overriding the one from URL
  const partnerOfferingID = offeringId || pageParams.offeringID;
  const partnerOffering = useGetPartnerOffering(partnerOfferingID);
  const { data: eventLayouts } = useGetPartnerEventLayouts(partnerOfferingID);

  const [toastShown, setToastShown] = useState(false);
  const cityByLocation = partnerOffering.offeringLocations.find(
    (l) => l.location.id === searchParams.locationID
  )?.location.city;
  const stateByCity = partnerOffering.offeringLocations.find(
    (l) => l.location.city === searchParams.city
  )?.location.state;
  const stateByLocation = partnerOffering.offeringLocations.find(
    (l) => l.location.id === searchParams.locationID
  )?.location.state;

  const { data: partnerOfferingFilterConfigs } =
    usePartnerOfferingFilterConfigsQuery(partnerOffering.id);
  const meetingRoomFilterConfig = getMeetingRoomFilterConfig(
    partnerOfferingFilterConfigs
  );
  const [foundResults, setFoundResults] = useState<number>(
    partnerOffering.offeringLocations.length
  );

  const filters = getFilterConfig(filterConfig, meetingRoomFilterConfig);

  const filter = useMemo(
    () =>
      deserializeMeetingRoomsFilterSearchParams({
        city: cityByLocation,
        state: filters.state ? stateByLocation || stateByCity : undefined,
        ...searchParams,
      }),
    [cityByLocation, filters.state, searchParams, stateByCity, stateByLocation]
  );

  const { data: partnerBookingPolicy } = usePartnerBookingPolicyQuery(
    partnerOffering.partner.id
  );
  const { data: partnerBookingHour } = usePartnerBookingHourQuery(
    partnerOffering.partner.id
  );
  const locations = partnerOffering.offeringLocations;

  const { data: bulkHourlyDiscountData } = useBulkHourlyDiscountQuery(
    partnerOffering.id
  );
  const bulkHourlyMinBookingUnit =
    bulkHourlyDiscountData?.featuredDynamicPriceRate.bookingCriteria[0]
      ?.minBookingUnit;
  const bulkHourlyDiscountValue =
    bulkHourlyDiscountData?.featuredDynamicPriceRate.dynamicPriceFormula
      .adjustmentType === DynamicPricingAdjustmentType.Percentage
      ? `${bulkHourlyDiscountData?.featuredDynamicPriceRate.dynamicPriceFormula.adjustmentValue}%`
      : undefined;

  const handleChangeFilter = useCallback(
    (filter: MeetingRoomsFilter) => {
      analytics.event(
        "Meeting Room Gallery Filter",
        { Filter: filter },
        { onlyProviders: ["mixpanel", "customerio"] }
      );

      const pathWithParams = buildPathWithParams(
        window.location.pathname,
        filter
      );
      history.replace(pathWithParams);
    },
    [history, analytics]
  );

  const handleCityChange = useCallback(
    (city?: string) => {
      handleChangeFilter({ ...filter, city, locationID: undefined });
    },
    [filter, handleChangeFilter]
  );

  const handleLocationChange = useCallback(
    (locationID?: string) => {
      handleChangeFilter({ ...filter, locationID });
    },
    [filter, handleChangeFilter]
  );

  const handlePressResetFilter = useCallback(() => {
    handleChangeFilter({
      state: undefined,
      city: undefined,
      cities: undefined,
      locationID: undefined,
      locationIDs: undefined,
      minCapacity: undefined,
      maxCapacity: undefined,
      date: undefined,
      startTime: undefined,
      endTime: undefined,
      startDate: undefined,
      endDate: undefined,
      layoutId: undefined,
    });
  }, [handleChangeFilter]);

  const states = uniqueBy(
    locations
      .map((l) => ({
        value: l.location.state,
        label: l.location.stateName,
        data: l.location,
      }))
      .sort((optionA, optionB) => optionA.label?.localeCompare(optionB.label)),
    (l) => l.value
  );

  const cities = uniqueBy(
    locations
      .map((l) => ({
        value: l.location.city as string,
        label: l.location.city as string,
        data: l.location,
      }))
      .filter(
        filter.state
          ? (l) => l.data.state === filter.state
          : (location) => location.data
      ),
    (l) => l.value
  );

  const hasFilter = !!(
    filter.minCapacity ||
    filter.maxCapacity ||
    filter.state ||
    filter.city ||
    filter.cities ||
    filter.locationID ||
    filter.locationIDs ||
    filter.date ||
    filter.startTime ||
    filter.startDate ||
    filter.layoutId
  );

  const { filters: filterConfigs, numberOfFilters } = useFilters(
    filters,
    cities,
    states,
    locations,
    eventLayouts || [],
    filter,
    handleChangeFilter
  );

  useEffect(() => {
    if (
      filter.locationID &&
      !cityByLocation &&
      partnerOffering.offeringLocations &&
      !toastShown
    ) {
      toast.warning({
        message: "The pre-selected location does not have meeting rooms",
      });
      setToastShown(true);
    }
  }, [
    cityByLocation,
    filter.locationID,
    partnerOffering.offeringLocations,
    toast,
    toastShown,
  ]);

  useEffect(() => {
    analytics.event(
      "Meeting Room Gallery View",
      {
        "Partner UUID": partnerId,
        "Partner Name": partnerOffering.partner.name,
        "Partner Internal Name": partnerOffering.partner.internalName,
        Source: "WL",
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
  }, [
    analytics,
    partnerId,
    partnerOffering.partner.internalName,
    partnerOffering.partner.name,
  ]);

  useEffect(() => {
    if (!searchParams.locationIDs) {
      if (filterConfigs.location?.options && cities) {
        if (
          filterConfigs.location.options.length === 1 &&
          !searchParams.locationID
        ) {
          handleLocationChange(locations[0].location.id);
        } else if (
          cities.length === 1 &&
          !searchParams.city &&
          !searchParams.locationID
        ) {
          handleCityChange(cities[0].value);
        }
      }
    }
  }, [
    cities,
    searchParams.city,
    handleCityChange,
    locations,
    searchParams.locationID,
    searchParams.locationIDs,
    handleLocationChange,
    filterConfigs.location?.options?.length,
    filterConfigs.location?.options,
  ]);

  const filterLocations = locations.map(toFilterLocationFromOfferingLocation);
  const maxCapacity = Math.max(
    ...locations
      .filter((item) => item.spaceMaxCapacity !== undefined)
      .map((item) => item.spaceMaxCapacity),
    0
  );

  return (
    <>
      <TemplateOfferingListingLayout
        partnerName={partnerName}
        locationAdress={locationAdress}
        title={partnerOffering.offeringTitle || fbt("Meeting Rooms")}
        hideBackButton={partnerConfig.meetingRoomGalleryHideBackButton}
        onLocaleSwitch={handlePressResetFilter}
        partnerConfig={partnerConfig}
        partnerOffering={partnerOffering}
        headerBar={
          bulkHourlyMinBookingUnit &&
          bulkHourlyDiscountValue &&
          showMeetingRoomGalleryBannerFeatureFlag && (
            <BulkDiscountBar
              title={`${fbt(
                "Book " +
                  fbt.param("numberOfHours", bulkHourlyMinBookingUnit) +
                  "+ hours, save " +
                  fbt.param("savingValue", bulkHourlyDiscountValue) +
                  " on select dates"
              )}*`}
              description={`*${fbt("Discounts subject to date availability")}`}
            />
          )
        }
        desktopFilters={
          numberOfFilters > 0 ? (
            meetingRoomGalleryImprovementsV2FeatureFlag ? (
              <>
                <GalleryDesktopFilters
                  partner={partnerOffering.partner}
                  filters={filterConfigs}
                  filterValues={filter}
                  locations={filterLocations}
                  onFilterValuesChange={handleChangeFilter}
                  partnerBookingPolicy={partnerBookingPolicy}
                  partnerBookingHour={partnerBookingHour}
                  onClearFilters={handlePressResetFilter}
                  maxCapacity={maxCapacity}
                  onContactUs={
                    partnerConfig.showTalkWithUsPrompt ||
                    partnerConfig.showTalkWithUsPrompt === undefined
                      ? () => setContactUsOpen(true)
                      : undefined
                  }
                />
              </>
            ) : (
              <DesktopFilters
                filters={filterConfigs}
                filterValues={filter}
                onFilterValuesChange={handleChangeFilter}
                partnerBookingPolicy={partnerBookingPolicy}
                partnerBookingHour={partnerBookingHour}
              />
            )
          ) : undefined
        }
        mobileFilters={
          numberOfFilters > 0 ? (
            meetingRoomGalleryImprovementsV2FeatureFlag ? (
              <GalleyMobileFilters
                partner={partnerOffering.partner}
                filters={filterConfigs}
                filterValues={filter}
                locations={filterLocations}
                onFilterValuesChange={handleChangeFilter}
                totalRooms={partnerOffering.offeringLocations.length}
                foundResults={foundResults}
                partnerBookingPolicy={partnerBookingPolicy}
                partnerBookingHour={partnerBookingHour}
                maxCapacity={maxCapacity}
                onContactUs={
                  partnerConfig.showTalkWithUsPrompt ||
                  partnerConfig.showTalkWithUsPrompt === undefined
                    ? () => setContactUsOpen(true)
                    : undefined
                }
              />
            ) : (
              <MobileFilters
                filters={filterConfigs}
                filterValues={filter}
                onFilterValuesChange={handleChangeFilter}
                totalRooms={partnerOffering.offeringLocations.length}
                foundResults={foundResults}
                partnerBookingPolicy={partnerBookingPolicy}
                partnerBookingHour={partnerBookingHour}
              />
            )
          ) : undefined
        }
        listingContent={
          <div data-testid="meeting-room-gallery">
            <Suspense
              fallback={
                <div className="container">
                  <PulseLoader />
                </div>
              }
            >
              <RoomsGallery
                hasFilter={hasFilter}
                partnerOfferingID={partnerOfferingID}
                filter={filter}
                onPressResetFilter={handlePressResetFilter}
                sortOrder={filters?.defaultSortOrder}
                featuredCapacity={partnerConfig.featuredCapacity}
                onFoundResults={setFoundResults}
                onChangeFilter={handleChangeFilter}
              />
            </Suspense>
          </div>
        }
        upsellSpaces={
          foundResults === 0 ? (
            meetingRoomGalleryImprovementsV2FeatureFlag ? (
              <div>
                <Spacer size={isMobile ? 48 : 40} />
                <SimilarRooms
                  filter={filter}
                  partnerOfferingId={partnerOfferingID}
                  sortOrder={filters.defaultSortOrder}
                />
              </div>
            ) : (
              <div>
                <Spacer size={isMobile ? 50 : 100} />
                <div className="fs-3 fw-semibold">
                  {fbt("Additional Spaces")}
                </div>
                <Spacer size={16} />
                <div>
                  {fbt(
                    "Here are some additional spaces you might be interested in."
                  )}
                </div>
                <Spacer size={isMobile ? 32 : 40} />
                <RoomsGallery
                  hasFilter={true}
                  partnerOfferingID={partnerOfferingID}
                  filter={{
                    state: filter.state,
                    city: filter.city,
                    locationID: filter.locationID,
                    layoutId: filter.layoutId,
                  }}
                  onPressResetFilter={handlePressResetFilter}
                  sortOrder={filters?.defaultSortOrder}
                  featuredCapacity={partnerConfig.featuredCapacity}
                  onChangeFilter={handleChangeFilter}
                />
              </div>
            )
          ) : undefined
        }
      />
      <ContactUsPrompt
        partnerID={partnerOffering.partner.id}
        messageableID={partnerOffering.id}
        hubspotForm={partnerConfig.hubspotForm}
        open={contactUsOpen}
        setOpen={setContactUsOpen}
      />
    </>
  );
}

interface RoomsGalleryProps {
  filter: MeetingRoomsFilter;
  partnerOfferingID: string;
  hasFilter: boolean;
  sortOrder?: SortOrder;
  featuredCapacity?: FeaturedCapacity;
  onPressResetFilter: () => void;
  onChangeFilter: (filter: MeetingRoomsFilter) => void;
  onFoundResults?: (numberOfResults: number) => void;
}

const SPACES_PER_PAGE = 20;
const CITY_PER_PAGE = 2;

interface CitySpace {
  city: string;
  spaces: LightSpace[];
}

function RoomsGallery(props: RoomsGalleryProps) {
  const {
    filter,
    hasFilter,
    partnerOfferingID,
    featuredCapacity,
    onPressResetFilter,
    sortOrder,
    onChangeFilter,
    onFoundResults,
  } = props;

  const { device } = useMediaQuery();

  const meetingRoomGalleryImprovementsV2FeatureFlag =
    useMrGalleryImprovementsV2FeatureFlag();

  const [hasMore, setHasMore] = useState(false);
  const partnerOffering = useGetPartnerOffering(
    partnerOfferingID,
    hasFilter ? filter : undefined
  );

  const getSpaces = useSpacesQueryCallback();
  const [fetchedSpaces, setFetchedSpaces] = useState<LightSpace[]>([]);
  const [citySpaces, setCitySpaces] = useState<CitySpace[]>([]);
  // This ref is used to prevent duplicate fetching the same data.
  // Because the component rerender many times at the beginning
  const fetchingSpacesRef = useRef<string[]>([]);
  const fetchingCitiesSpace = useRef<string[]>();

  const offeringLocationsGroupedByCity = useMemo(
    () =>
      groupBy(partnerOffering.offeringLocations, (l) => l.location.city || ""),
    [partnerOffering]
  );

  for (const city in offeringLocationsGroupedByCity) {
    const locations = offeringLocationsGroupedByCity[city];
    locations.sort((spaceA, spaceB) => {
      const capacitySpaceA = spaceA.spaceMaxCapacity;
      const capacitySpaceB = spaceB.spaceMaxCapacity;
      if (sortOrder === SortOrder.HighestToLowest) {
        return capacitySpaceB - capacitySpaceA;
      }
      return capacitySpaceA - capacitySpaceB;
    });
  }

  const cities = useMemo(
    () => keys(offeringLocationsGroupedByCity),
    [offeringLocationsGroupedByCity]
  );

  const fetchedSpacesWithCity = fetchedSpaces.map((s) => ({
    city:
      partnerOffering.offeringLocations.find((l) => l.spaceID === s.id)
        ?.location.city || "",
    space: s,
  }));
  const citySpacesV2 = cities
    .map((c) => ({
      city: c,
      spaces: fetchedSpacesWithCity
        .filter((element) => element.city === c)
        .map((element) => element.space),
    }))
    .filter((c) => c.spaces.length);

  const fetchMoreDataV2 = useCallback(async () => {
    const numberOfSpaces = partnerOffering.offeringLocations.length;
    if (fetchedSpaces.length === numberOfSpaces && cities.length !== 0) {
      setHasMore(false);
      return;
    }

    const start = fetchedSpaces.length;
    const end = Math.min(start + SPACES_PER_PAGE, numberOfSpaces);
    const fetchingSpaceIDs = Object.values(offeringLocationsGroupedByCity)
      .flat()
      .map((l) => l.spaceID)
      .slice(start, end);
    if (isEqual(fetchingSpacesRef.current, fetchingSpaceIDs)) {
      return;
    }

    fetchingSpacesRef.current = fetchingSpaceIDs;
    const data = await getSpaces(fetchingSpaceIDs);

    const sortedByInputIds: LightSpace[] = [];
    const spaceMapById = new Map(data.map((space) => [space.id, space]));
    fetchingSpaceIDs.forEach((id) => {
      const space = spaceMapById.get(id);
      if (space) {
        sortedByInputIds.push(space);
      }
    });

    fetchingSpacesRef.current = [];
    setFetchedSpaces((value) => [...value, ...sortedByInputIds]);
  }, [
    offeringLocationsGroupedByCity,
    fetchedSpaces.length,
    getSpaces,
    cities.length,
    partnerOffering.offeringLocations.length,
  ]);

  const fetchMoreData = useCallback(async () => {
    if (citySpaces.length === cities.length && cities.length !== 0) {
      setHasMore(false);
      return;
    }

    const start = citySpaces.length;
    const end = Math.min(citySpaces.length + CITY_PER_PAGE, cities.length);
    const fetchingCities = cities.slice(start, end);
    if (isEqual(fetchingCitiesSpace.current, fetchingCities)) {
      return;
    }

    fetchingCitiesSpace.current = fetchingCities;
    const data = await Promise.all(
      fetchingCities.map(async (city) => {
        const cityLocations = offeringLocationsGroupedByCity[city];

        const spaceIDs = cityLocations.map((l) => l.spaceID);
        const spaces = await getSpaces(spaceIDs);

        return {
          city,
          spaces,
        };
      })
    );

    if (isEqual(fetchingCitiesSpace.current, fetchingCities)) {
      fetchingCitiesSpace.current = [];
      setCitySpaces([...citySpaces, ...data]);
    }
  }, [citySpaces, cities, getSpaces, offeringLocationsGroupedByCity]);

  useEffect(() => {
    fetchingCitiesSpace.current = [];
    setFetchedSpaces([]);
    setCitySpaces([]);
    setHasMore(true);
    onFoundResults?.(partnerOffering.offeringLocations.length);
  }, [
    filter,
    setHasMore,
    setCitySpaces,
    partnerOffering.offeringLocations.length,
    onFoundResults,
  ]);

  useEffect(() => {
    if (hasMore && fetchedSpaces.length === 0) {
      fetchMoreDataV2();
    }
  }, [hasMore, fetchedSpaces.length, fetchMoreDataV2]);

  if (isEmpty(partnerOffering.offeringLocations)) {
    return (
      <div className={device !== "mobile" ? "container" : undefined}>
        <EmptyMeetingRooms
          hasFilter={hasFilter}
          filter={filter}
          onClearFilters={onPressResetFilter}
          onClearOneFilter={onChangeFilter}
        />
      </div>
    );
  }

  return (
    <div className="container">
      <InfiniteScroll
        loadMore={
          meetingRoomGalleryImprovementsV2FeatureFlag
            ? fetchMoreDataV2
            : fetchMoreData
        }
        hasMore={hasMore}
        loader={<PulseLoader />}
      >
        {(meetingRoomGalleryImprovementsV2FeatureFlag
          ? citySpacesV2
          : citySpaces
        )
          .filter(({ city }) => !isNil(offeringLocationsGroupedByCity[city]))
          .map(({ city, spaces }) => {
            const offeringLocations = offeringLocationsGroupedByCity[city];

            return (
              <div key={city}>
                <RoomGalleryItemList
                  key={city}
                  spaces={spaces}
                  partnerOfferingLocations={offeringLocations}
                  city={city}
                  partnerOffering={partnerOffering}
                  partnerOfferingID={partnerOfferingID}
                  filter={filter}
                  featuredCapacity={featuredCapacity}
                />
              </div>
            );
          })}
      </InfiniteScroll>
    </div>
  );
}

interface RoomGalleryItemListProps {
  city: string;
  partnerOfferingID: string;
  partnerOffering: PartnerOffering;
  partnerOfferingLocations: PartnerOfferingLocation[];
  filter: MeetingRoomsFilter;
  featuredCapacity?: FeaturedCapacity;
  spaces: LightSpace[];
}

function RoomGalleryItemList(props: RoomGalleryItemListProps) {
  const {
    city,
    partnerOfferingID,
    partnerOffering,
    partnerOfferingLocations,
    featuredCapacity,
    filter,
    spaces,
  } = props;

  const featuredSpaces = featuredCapacity
    ? spaces.filter(
        (s) =>
          s.layouts[0].capacity <= featuredCapacity?.maxCapacity &&
          s.layouts[0].capacity >= featuredCapacity?.minCapacity
      )
    : [];
  const filteredSpaces = spaces.filter((s) => featuredSpaces.indexOf(s) === -1);

  return (
    <div>
      <h2 className="location-name">{city}</h2>
      <Spacer size={16} />
      {featuredCapacity && featuredSpaces.length > 0 && (
        <Fragment>
          <div className="row row-cols-md-3">
            {featuredSpaces.map((space) => (
              <RoomGroup
                space={space}
                partnerOffering={partnerOffering}
                partnerOfferingID={partnerOfferingID}
                partnerOfferingLocations={partnerOfferingLocations}
                filter={filter}
              />
            ))}
          </div>
          <Spacer size={16} />
        </Fragment>
      )}
      <div className="row row-cols-md-3">
        {filteredSpaces.map((space) => (
          <RoomGroup
            space={space}
            partnerOffering={partnerOffering}
            partnerOfferingID={partnerOfferingID}
            partnerOfferingLocations={partnerOfferingLocations}
            filter={filter}
          />
        ))}
      </div>
      <Spacer size={24} />
    </div>
  );
}

interface RoomGroupProps {
  space: LightSpace;
  partnerOfferingID: string;
  partnerOffering: PartnerOffering;
  partnerOfferingLocations: PartnerOfferingLocation[];
  filter: MeetingRoomsFilter;
}

export function RoomGroup(props: RoomGroupProps) {
  const {
    space,
    partnerOfferingID,
    partnerOffering,
    partnerOfferingLocations,
    filter,
  } = props;
  const analytics = useAnalytics();
  const featuredPromoDescription = function (spaceID: string) {
    const spaceOfferingLocation = partnerOfferingLocations.find(
      (p) => p.spaceID === spaceID
    );
    return spaceOfferingLocation?.featuredPromoDescription;
  };

  const offeringDetailsUrl = buildPathWithParams(
    `/offerings/${partnerOfferingID}/spaces/${space.id}`,
    filter
  );

  const { pathname, search } = new URL(
    offeringDetailsUrl,
    window.location.origin
  );

  const handleClick = useCallback(() => {
    if (partnerOffering.partner.partnerSlug === "the-square") {
      analytics.event("AddToWishlist", {}, { onlyProviders: ["pixel"] });
    }
  }, [analytics, partnerOffering.partner.partnerSlug]);

  return (
    <div style={{ paddingBottom: 16 }}>
      <Link
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          textDecoration: "none",
          flex: 1,
        }}
        onClick={handleClick}
        to={{
          pathname,
          search,
          state: { comingFrom: COMING_FROM_GALLERY },
        }}
      >
        <div
          style={{
            flex: 1,
            borderWidth: 1,
            borderRadius: 5,
            borderColor: "#D7D5DD",
            height: "100%",
          }}
        >
          <Suspense
            fallback={
              <div style={{ justifyContent: "center" }}>
                <PulseLoader />
              </div>
            }
          >
            <RoomGalleryItem
              space={space}
              featuredPromoDescription={featuredPromoDescription(space.id)}
              partnerOfferingID={partnerOffering.id}
              partnerID={partnerOffering.partner.id}
              template={partnerOffering.template}
            />
          </Suspense>
        </div>
      </Link>
    </div>
  );
}

interface RoomGalleryItemProps {
  space: LightSpace;
  partnerOfferingID: string;
  featuredPromoDescription?: string;
  partnerID: string;
  template: Template;
}

function RoomGalleryItem(props: RoomGalleryItemProps) {
  const {
    space,
    featuredPromoDescription,
    partnerID,
    partnerOfferingID,
    template,
  } = props;
  const { fbt } = useI18n();

  const hourlyPrice = space.pricings.find((p) => p.type === PricingType.Hourly);
  const priceRanges = space.priceRanges;

  return (
    <Card
      style={{ height: "100%" }}
      bg="light"
      border="light"
      text="dark"
      className="meeting-room-card"
    >
      {featuredPromoDescription && (
        <OfferingRibbon ribbonMessage={featuredPromoDescription} />
      )}
      <Card.Img
        variant="top"
        src={space.images[0].small.url}
        title={space.name}
        style={{
          objectFit: "cover",
          width: "100%",
          height: 193,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        }}
      />
      <Card.Body className="d-flex flex-column">
        <div>
          <Card.Title className="fw-bold fs-6 meeting-room-card-title">
            {space.name}
          </Card.Title>
          <Card.Subtitle className="meeting-room-card-sub-title">
            {space.streetAddress}
          </Card.Subtitle>
        </div>
        <Card.Text>
          <Spacer size={16} />
          <GalleryRoomCardDetails
            partnerID={partnerID}
            capacity={getMaxCapacity(space)}
            currency={space.currencyCode}
            partnerOfferingID={partnerOfferingID}
            priceRanges={priceRanges}
            hourlyPrice={
              space.priceRanges.hourly
                ? space.priceRanges.hourly.lowestPrice
                : hourlyPrice?.price
            }
            dailyPrice={priceRanges.daily?.lowestPrice}
            bulkDiscount={
              space.dynamicPriceRates &&
              getSpaceBulkDiscount(
                space.dynamicPriceRates,
                fbt,
                space.currencyCode || undefined
              )
            }
            template={template}
          />
        </Card.Text>
        <Button style={{ width: "100%", marginTop: "auto" }} variant="primary">
          {fbt("Book here")}
        </Button>
      </Card.Body>
    </Card>
  );
}
